import React, { useState, useMemo } from 'react'
import { useWallet } from '@solana/wallet-adapter-react'
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  Filter,
  MoreHorizontal,
  Copy,
  TrendingUp,
  Calendar,
  User,
  Award,
  Zap,
  RefreshCw,
  Download
} from 'lucide-react'
import { cn } from '../lib/utils'
import { UIContributor } from '../lib/poolDataTransformers'

/**
 * Props for the DonorTable component
 */
interface DonorTableProps {
  donors: UIContributor[]
  loading?: boolean
  className?: string
  showSearch?: boolean
  showFilters?: boolean
  showPagination?: boolean
  pageSize?: number
  onDonorClick?: (donor: UIContributor) => void
  refreshable?: boolean
  onRefresh?: () => void
}

type SortField = 'amount' | 'percentage' | 'timestamp' | 'totalDonations'
type SortDirection = 'asc' | 'desc'

/**
 * Enhanced DonorTable Component - Professional data table with glassmorphism design
 * 
 * This component provides:
 * - Glassmorphism styling with smooth hover effects
 * - Advanced sorting and filtering capabilities
 * - Professional data visualization
 * - Responsive design with mobile-friendly layouts
 * - Interactive animations and micro-interactions
 * - Accessibility-compliant table structure
 * - Real-time data updates with loading states
 */
export function DonorTable({
  donors,
  loading = false,
  className,
  showSearch = true,
  showFilters = true,
  showPagination = true,
  pageSize = 10,
  onDonorClick,
  refreshable = false,
  onRefresh
}: DonorTableProps): JSX.Element {
  const { connected: _connected } = useWallet()
  const [searchTerm, setSearchTerm] = useState('')
  const [sortField, setSortField] = useState<SortField>('amount')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [showOnlyTop, setShowOnlyTop] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [selectedDonors, _setSelectedDonors] = useState<Set<string>>(new Set())

  /**
   * Handle sorting column clicks
   * Toggles sort direction if same field, otherwise sets new field
   */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('desc')
    }
  }

  /**
   * Handle refresh action
   */
  const handleRefresh = async () => {
    if (onRefresh) {
      setRefreshing(true)
      await onRefresh()
      setTimeout(() => setRefreshing(false), 500)
    }
  }

  /**
   * Filter and sort donors based on current state
   */
  const filteredAndSortedDonors = useMemo(() => {
    let filtered = donors

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(donor =>
        donor.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
        donor.transactionHash.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    // Apply top donor filter
    if (showOnlyTop) {
      filtered = filtered.filter(donor => donor.isTopDonor)
    }

    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField]
      let bValue = b[sortField]

      if (sortField === 'timestamp') {
        aValue = new Date(aValue as Date).getTime()
        bValue = new Date(bValue as Date).getTime()
      }

      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0
      }
    })

    return filtered
  }, [donors, searchTerm, sortField, sortDirection, showOnlyTop])

  /**
   * Paginated donors for current page
   */
  const paginatedDonors = useMemo(() => {
    if (!showPagination) return filteredAndSortedDonors
    
    const startIndex = (currentPage - 1) * pageSize
    return filteredAndSortedDonors.slice(startIndex, startIndex + pageSize)
  }, [filteredAndSortedDonors, currentPage, pageSize, showPagination])

  /**
   * Format currency values for display
   */
  const formatCurrency = (value: number): string => {
    if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K SOL`
    }
    return `${value.toFixed(2)} SOL`
  }

  /**
   * Format wallet address for display
   */
  const formatAddress = (address: string): string => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  /**
   * Format relative time
   */
  const formatRelativeTime = (date: Date): string => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const days = Math.floor(diff / (24 * 60 * 60 * 1000))
    const hours = Math.floor(diff / (60 * 60 * 1000))
    const minutes = Math.floor(diff / (60 * 1000))

    if (days > 0) return `${days}d ago`
    if (hours > 0) return `${hours}h ago`
    return `${minutes}m ago`
  }

  /**
   * Copy text to clipboard
   */
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      // Could add a toast notification here
    } catch (err) {
      console.error('Failed to copy text: ', err)
    }
  }

  /**
   * Table header component with sorting indicators
   */
  const TableHeader: React.FC<{
    field: SortField
    label: string
    className?: string
  }> = ({ field, label, className: headerClassName }) => (
    <th
      className={cn(
        "group cursor-pointer select-none px-6 py-4 text-left",
        "hover:bg-dark-panel/30 transition-all duration-200",
        "text-sm font-semibold text-cyber-cyan uppercase tracking-wider",
        headerClassName
      )}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-2">
        <span className="group-hover:text-cyber-cyan transition-colors duration-200">
          {label}
        </span>
        <div className="flex flex-col">
          {sortField === field ? (
            sortDirection === 'asc' ? (
              <ArrowUp className="w-3 h-3 text-cyber-cyan" />
            ) : (
              <ArrowDown className="w-3 h-3 text-cyber-cyan" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 text-text-muted group-hover:text-cyber-cyan transition-colors duration-200" />
          )}
        </div>
      </div>
    </th>
  )

  if (loading) {
    return (
      <div className={cn("space-y-6", className)}>
        {/* Loading Header */}
        <div className="flex items-center justify-between">
          <div className="h-8 bg-gradient-to-r from-dark-surface to-dark-border rounded animate-shimmer w-48" />
          <div className="h-10 bg-gradient-to-r from-dark-surface to-dark-border rounded-cyber animate-shimmer w-32" />
        </div>
        
        {/* Loading Table */}
        <div className="glass-card overflow-hidden">
          <div className="space-y-4 p-6">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="h-10 bg-gradient-to-r from-dark-surface to-dark-border rounded animate-shimmer w-16" />
                <div className="h-10 bg-gradient-to-r from-dark-surface to-dark-border rounded animate-shimmer flex-1" />
                <div className="h-10 bg-gradient-to-r from-dark-surface to-dark-border rounded animate-shimmer w-24" />
                <div className="h-10 bg-gradient-to-r from-dark-surface to-dark-border rounded animate-shimmer w-20" />
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn("space-y-4 lg:space-y-6", className)}>
      {/* Header with Controls */}
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-cyber-gradient rounded-cyber flex items-center justify-center">
            <User className="w-5 h-5 text-dark-bg" />
          </div>
          <div>
            <h3 className="text-xl font-header text-gradient-cyber">Contributors</h3>
            <p className="text-sm text-text-muted">
              {filteredAndSortedDonors.length} total contributors
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          {showSearch && (
            <div className="relative flex-1 min-w-[200px] sm:min-w-[250px]">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-muted" />
              <input
                type="text"
                placeholder="Search address or transaction..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="cyber-input pl-10 w-64"
              />
            </div>
          )}

          {/* Filters */}
          {showFilters && (
            <button
              onClick={() => setShowOnlyTop(!showOnlyTop)}
              className={cn(
                "flex items-center space-x-2 px-3 py-2 rounded-cyber transition-all duration-300",
                showOnlyTop
                  ? "bg-cyber-cyan/20 text-cyber-cyan border border-cyber-cyan/30"
                  : "glass-dark border border-dark-border/30 text-text-secondary hover:border-cyber-cyan/30"
              )}
            >
              <Filter className="w-4 h-4" />
              <span className="text-sm">Top Only</span>
            </button>
          )}

          {/* Refresh Button */}
          {refreshable && (
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center justify-center w-10 h-10 rounded-cyber glass-dark hover:bg-dark-panel/70 transition-all duration-300 focus-cyber disabled:opacity-50"
              aria-label="Refresh data"
            >
              <RefreshCw className={cn(
                "w-4 h-4 text-text-secondary",
                refreshing && "animate-spin"
              )} />
            </button>
          )}

          {/* Export Button */}
          <button className="flex items-center justify-center w-10 h-10 rounded-cyber glass-dark hover:bg-dark-panel/70 transition-all duration-300 focus-cyber">
            <Download className="w-4 h-4 text-text-secondary" />
          </button>
        </div>
      </div>

      {/* Enhanced Table */}
      <div className="cyber-table-container overflow-hidden rounded-cyber">
        <div className="overflow-x-auto -mx-2 sm:mx-0">
          <table className="cyber-table w-full min-w-[640px]">
            <thead>
              <tr>
                <th className="w-16 text-center px-6 py-4 text-sm font-semibold text-cyber-cyan uppercase tracking-wider">#</th>
                <TableHeader field="amount" label="Amount" />
                <th className="px-6 py-4 text-left text-sm font-semibold text-cyber-cyan uppercase tracking-wider">Contributor</th>
                <TableHeader field="percentage" label="Share %" />
                <TableHeader field="timestamp" label="Last Donation" />
                <TableHeader field="totalDonations" label="Total" />
                <th className="w-12 px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {paginatedDonors.map((donor, index) => {
                const globalIndex = (currentPage - 1) * pageSize + index + 1
                const isSelected = selectedDonors.has(donor.id)
                
                return (
                  <tr
                    key={donor.id}
                    className={cn(
                      "group cursor-pointer transition-all duration-200",
                      "hover:bg-dark-panel/20",
                      isSelected && "bg-cyber-cyan/5",
                      onDonorClick && "cursor-pointer"
                    )}
                    onClick={() => onDonorClick?.(donor)}
                  >
                    {/* Rank */}
                    <td className="text-center px-6 py-4">
                      <div className={cn(
                        "w-8 h-8 rounded-cyber flex items-center justify-center text-sm font-bold transition-all duration-200",
                        globalIndex <= 3 && "text-cyber-yellow bg-cyber-yellow/20 group-hover:bg-cyber-yellow/30",
                        globalIndex > 3 && "text-text-muted bg-dark-surface group-hover:bg-dark-panel"
                      )}>
                        {globalIndex <= 3 ? (
                          <Award className="w-4 h-4" />
                        ) : (
                          globalIndex
                        )}
                      </div>
                    </td>

                    {/* Amount */}
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <span className="metric-display text-lg group-hover:text-glow transition-all duration-200">
                          {formatCurrency(donor.amount)}
                        </span>
                        {donor.amount > 100 && (
                          <TrendingUp className="w-4 h-4 text-status-success" />
                        )}
                      </div>
                    </td>

                    {/* Contributor */}
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          {donor.isTopDonor ? (
                            <div className="w-8 h-8 bg-cyber-gradient rounded-cyber flex items-center justify-center flex-shrink-0">
                              <Zap className="w-4 h-4 text-dark-bg" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 bg-dark-surface rounded-cyber flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-text-muted" />
                            </div>
                          )}
                        </div>
                        
                        <div className="min-w-0">
                          <div className="flex items-center space-x-2">
                            <code className="font-mono text-sm text-text-primary group-hover:text-cyber-cyan transition-colors duration-200 truncate">
                              {formatAddress(donor.address)}
                            </code>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                copyToClipboard(donor.address)
                              }}
                              className="opacity-0 group-hover:opacity-100 p-1 hover:bg-dark-panel rounded transition-all duration-200"
                            >
                              <Copy className="w-3 h-3 text-text-muted hover:text-cyber-cyan" />
                            </button>
                          </div>
                          {donor.isTopDonor && (
                            <div className="flex items-center space-x-1 text-xs text-cyber-green">
                              <Award className="w-3 h-3" />
                              <span>Top Contributor</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Percentage */}
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-cyber-cyan whitespace-nowrap">
                          {donor.percentage.toFixed(2)}%
                        </span>
                        <div className="flex-1 max-w-16 sm:max-w-20">
                          <div className="progress-bar h-1.5">
                            <div 
                              className="progress-fill h-full"
                              style={{ width: `${Math.min(donor.percentage * 2, 100)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Last Donation */}
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-4 h-4 text-text-muted" />
                        <div>
                          <div className="text-sm text-text-primary">
                            {formatRelativeTime(donor.timestamp)}
                          </div>
                          <div className="text-xs text-text-muted">
                            {donor.timestamp.toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Total Donations */}
                    <td className="px-6 py-4">
                      <div className="text-right">
                        <div className="text-sm font-mono text-text-primary">
                          {donor.totalDonations}
                        </div>
                        <div className="text-xs text-text-muted">
                          donations
                        </div>
                      </div>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            // Handle action menu
                          }}
                          className="opacity-0 group-hover:opacity-100 p-2 hover:bg-dark-panel rounded transition-all duration-200"
                        >
                          <MoreHorizontal className="w-4 h-4 text-text-muted hover:text-cyber-cyan" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {showPagination && Math.ceil(filteredAndSortedDonors.length / pageSize) > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-text-muted">
            Showing {(currentPage - 1) * pageSize + 1} to{' '}
            {Math.min(currentPage * pageSize, filteredAndSortedDonors.length)} of{' '}
            {filteredAndSortedDonors.length} contributors
          </div>
          
          <div className="flex flex-wrap items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="cyber-button-secondary px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.ceil(filteredAndSortedDonors.length / pageSize) }, (_, i) => i + 1)
                .filter(page => 
                  page === 1 || 
                  page === Math.ceil(filteredAndSortedDonors.length / pageSize) || 
                  Math.abs(page - currentPage) <= 2
                )
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && (
                      <span className="text-text-muted">...</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={cn(
                        "w-8 h-8 rounded text-sm transition-all duration-200",
                        currentPage === page
                          ? "bg-cyber-cyan text-dark-bg"
                          : "text-text-muted hover:text-cyber-cyan hover:bg-dark-panel/30"
                      )}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
            </div>
            
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === Math.ceil(filteredAndSortedDonors.length / pageSize)}
              className="cyber-button-secondary px-3 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {paginatedDonors.length === 0 && !loading && (
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 bg-dark-surface rounded-cyber flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-text-muted" />
          </div>
          <h4 className="text-lg font-medium text-text-primary mb-2">No Contributors Found</h4>
          <p className="text-text-muted">
            {searchTerm || showOnlyTop
              ? "Try adjusting your search or filter criteria."
              : "This pool doesn't have any contributors yet."}
          </p>
        </div>
      )}
    </div>
  )
}

export default DonorTable